/**
 * Team points — the Scorecard (D56).
 *
 * **A ledger, not a counter.** Every award is a row; the standings are the sum
 * of them, computed on read. The obvious alternative was eight counters and an
 * atomic `ADD`, which is cheaper per read and cannot be undone: awarding is the
 * first action in this app that repeating does not reverse, so a mis-tapped 500
 * would have to be corrected by writing -500 — a lie in the audit sense and a
 * number nobody can explain a week later. The ledger gives standings, history
 * and a real undo off one list, with no second source of truth to drift from
 * the first. That is the same argument `guests.ts` makes about upload counts.
 *
 * It is affordable because the volume is known and small: **around fifty awards
 * across the week**, all of them team-level. One Query returns the board.
 */
import type { Team } from './identity.js';

/**
 * The only amounts that can be awarded.
 *
 * A fixed list rather than a free number, and validated server-side against
 * this constant for the reason the team route gives about `TEAMS`: the value is
 * written straight into storage and summed into a total every guest sees, so an
 * unchecked one puts an arbitrary number on the board with no error anywhere.
 */
export const AWARD_POINTS = [50, 100, 200, 500] as const;
export type AwardPoints = typeof AWARD_POINTS[number];

/** One award. Team-level: there is deliberately no guest dimension here (D56). */
export interface Award {
  /**
   * **Generated by the client** and written under a condition that the row does
   * not already exist, which is what makes the award idempotent. A double-tap
   * on hotel wifi otherwise awards 500 twice, and at fifty transactions across
   * the week one duplicate row is two percent of the whole board rather than a
   * rounding error.
   */
  awardId: string;
  team: Team;
  points: AwardPoints;
  awardedAt: number;
  /**
   * Which organiser awarded it. **The `userId`, never a denormalised name** —
   * D55 was an entire change about copied profile going stale, and an audit
   * field nothing renders is not worth re-opening that for.
   */
  awardedBy: string;
}

export interface TeamScore { team: Team; points: number }

/**
 * What every guest reads. **All eight teams, including the ones on nothing** —
 * a board missing Spain because nobody has scored reads as a bug rather than as
 * a zero.
 *
 * Ordered by points descending, ties broken by `TEAMS` order so the board is
 * stable between polls: at fifty awards ties are the normal case, not the edge,
 * and rows swapping places under a reader who is not looking away is worse than
 * an arbitrary-but-fixed order.
 */
export interface ScoresResponse { standings: TeamScore[] }

/** The ledger itself, admin-only. Guests never receive it — the board they read
 *  is standings, and a list they cannot render is a list not worth sending. */
export interface AwardsResponse { awards: Award[] }

export interface AwardPointsRequest {
  awardId: string;
  team: Team;
  points: AwardPoints;
}

/** Both writes answer with the settled board, so the sheet can show the new
 *  total without a second round trip — the same shape `LikeResponse` uses. */
export interface AwardMutationResponse { ok: true; standings: TeamScore[] }
